import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';
import { AdBanner } from '../components/ads/AdBanner';
import { ADS_ENABLED } from '../config/ads';
import { Stat } from '../components/ui/Stat';
import { HeroDemoPanel } from '../components/home/HeroDemoPanel';

// What candidates can practice for — honest, keyword-relevant (no fabricated logos/partnerships).
const PRACTICE_FOR = [
  'Software Engineering',
  'Product Management',
  'Design',
  'Behavioral rounds',
  'System Design',
  'Coding',
];

// Who it's for — answers "what is this and is it for me?" at a glance.
const AUDIENCE = [
  { icon: 'code', label: 'Software Engineers', desc: 'Coding, system design and behavioral rounds.' },
  { icon: 'rocket_launch', label: 'Product Managers', desc: 'Product sense, execution and estimation.' },
  { icon: 'palette', label: 'Designers', desc: 'Portfolio walkthroughs and app critiques.' },
];

// How the free demo actually works (matches the real guest flow).
const STEPS = [
  {
    title: 'Pick your interview',
    desc: 'Choose the type — behavioral, technical, or system design — and the role you are targeting.',
  },
  {
    title: 'Answer in your own words',
    desc: 'The AI asks realistic questions and adapts to your answers, like a real interviewer would.',
  },
  {
    title: 'Get instant feedback',
    desc: 'See scoring and concrete tips as you go. Create a free account to keep practicing, or book a real expert.',
  },
];

export function Home() {
  const { isAuthenticated } = useAuth();

  return (
    <>
      <SEO
        title="InterviewR - AI Mock Interviews & Expert Coaching"
        description="Practice with real-time AI mock interviews or book sessions with industry experts. Land your dream role at top tech companies."
        url={typeof window !== 'undefined' ? window.location.href : 'https://interview-r.com'}
      />
      <StructuredData type="website" />
      <StructuredData type="organization" />
      <StructuredData type="service" />
      <StructuredData type="webApplication" />

      {/* Landing-only display font + one well-orchestrated load reveal. Respects reduced-motion. */}
      <style>{`
        .hr-display { font-family: 'Bricolage Grotesque', ui-sans-serif, system-ui, -apple-system, sans-serif; letter-spacing: -0.02em; }
        @keyframes hr-rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
        @keyframes hr-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .hr-rise { opacity: 0; animation: hr-rise 0.7s cubic-bezier(0.33, 1, 0.68, 1) forwards; }
        .hr-float { animation: hr-float 5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .hr-rise { animation: none; opacity: 1; }
          .hr-float { animation: none; }
        }
        /* Hero demo panel micro-animations — motion-gated twice: these classes only exist
           under no-preference, and the JS loop never starts under reduce. */
        @media (prefers-reduced-motion: no-preference) {
          @keyframes hr-dot { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-3px); } }
          .hr-dot { animation: hr-dot 0.9s ease-in-out infinite; }
          @keyframes hr-caret-blink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
          .hr-caret { animation: hr-caret-blink 1s steps(2, start) infinite; }
          @keyframes hr-eq { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }
          .hr-eq { animation: hr-eq 0.6s ease-in-out infinite; }
          @keyframes hr-breathe { from { opacity: 0.65; transform: scale(1); } to { opacity: 1; transform: scale(1.03); } }
          .hr-breathe { animation: hr-breathe 7s ease-in-out infinite alternate; }
          @keyframes hr-float-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
          .hr-float-slow { animation: hr-float-slow 6s ease-in-out infinite; }
          @keyframes hr-sheen { from { transform: rotate(20deg) translateX(-150%); } to { transform: rotate(20deg) translateX(250%); } }
          .hr-sheen { animation: hr-sheen 1.2s ease-in-out 1 both; }
        }
      `}</style>

      <div className="min-h-screen bg-stitch-canvas">
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden pt-12 pb-20 sm:pt-16 sm:pb-24 lg:pt-20 lg:pb-28">
          {/* atmospheric backdrop: color mesh + dot texture */}
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-40 right-[-10%] h-[40rem] w-[40rem] rounded-full bg-stitch-accent/15 blur-[130px]" />
            <div className="absolute left-[-15%] top-32 h-[34rem] w-[34rem] rounded-full bg-stitch-primary/15 blur-[130px]" />
            <div className="absolute inset-0 opacity-[0.04] [background-image:radial-gradient(circle_at_1px_1px,rgb(15_23_42)_1px,transparent_0)] [background-size:22px_22px]" />
          </div>

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
              {/* Left: message */}
              <div className="relative z-10">
                <span
                  className="hr-rise mb-6 inline-flex max-w-full items-center rounded-full border border-stitch-accent/20 bg-stitch-accent-soft px-3 py-1 text-xs font-semibold uppercase tracking-wider text-stitch-accent"
                  style={{ animationDelay: '0ms' }}
                >
                  <span
                    className="material-symbols-outlined mr-1 text-sm"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    auto_awesome
                  </span>
                  <span className="sm:hidden">AI mocks + real coaches</span>
                  <span className="hidden sm:inline">AI mock interviews + real expert coaches</span>
                </span>

                <h1
                  className="hr-display hr-rise mb-6 text-4xl font-extrabold leading-[1.05] text-stitch-fg sm:text-5xl md:text-6xl"
                  style={{ animationDelay: '80ms' }}
                >
                  Walk in already
                  <br />
                  having done{' '}
                  <span className="bg-gradient-to-r from-stitch-primary to-stitch-accent bg-clip-text text-transparent">
                    the interview.
                  </span>
                </h1>

                <p
                  className="hr-rise mb-9 max-w-xl text-lg leading-relaxed text-stitch-muted sm:text-xl"
                  style={{ animationDelay: '160ms' }}
                >
                  InterviewR runs realistic AI mock interviews for software engineers, PMs and
                  designers — with instant, scored feedback on every answer. Practice for free, then
                  book a real interviewer when it counts.
                </p>

                <div
                  className="hr-rise flex flex-col items-start gap-4 sm:flex-row sm:items-center"
                  style={{ animationDelay: '240ms' }}
                >
                  <Link
                    to="/try-interview"
                    className="flex items-center justify-center rounded-stitch-xl bg-stitch-primary px-8 py-4 text-lg font-bold text-white shadow-xl shadow-stitch-primary/20 transition-all hover:bg-stitch-primary-hover active:scale-[0.98]"
                  >
                    Try a free AI interview
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="ml-2 h-5 w-5"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="7" />
                      <path d="M9.5 11.5h.01M14.5 11.5h.01M9.5 14.5c.8.7 1.6 1 2.5 1s1.7-.3 2.5-1" />
                      <path d="M12 3v2M5 8l1.4 1.4M19 8l-1.4 1.4" />
                    </svg>
                  </Link>
                  <Link
                    to={isAuthenticated ? '/interviewers' : '/register'}
                    className="inline-flex items-center text-base font-semibold text-stitch-fg-soft transition-colors hover:text-stitch-primary"
                  >
                    or browse expert coaches
                    <span className="material-symbols-outlined ml-1 text-lg">arrow_forward</span>
                  </Link>
                </div>

                <p
                  className="hr-rise mt-4 flex items-center gap-1.5 text-sm text-stitch-muted"
                  style={{ animationDelay: '300ms' }}
                >
                  <span className="material-symbols-outlined text-base text-stitch-success">check_circle</span>
                  No sign-up needed to try the demo.
                </p>

                <div
                  className="hr-rise mt-10 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-3"
                  style={{ animationDelay: '360ms' }}
                >
                  <Stat label="AI mock demo" value="Free" hint="no sign-up to try" />
                  <Stat label="Feedback" value="Instant" hint="scored as you answer" />
                  <Stat label="Expert sessions" value="1:1" hint="book a real interviewer" />
                </div>
              </div>

              {/* Right: self-playing mock-interview demo panel */}
              <HeroDemoPanel />
            </div>
          </div>
        </section>

        {/* ── What is InterviewR (clarity band) ──────────────────────────── */}
        <section className="border-y border-stitch-border bg-stitch-surface py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-sm font-semibold uppercase tracking-widest text-stitch-primary">
                What is InterviewR?
              </span>
              <p className="hr-display mt-3 text-2xl font-bold leading-snug text-stitch-fg sm:text-3xl">
                A practice gym for job interviews. Rehearse with an AI that interviews you like the
                real thing — and book a human expert when you want a second opinion.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3">
              {AUDIENCE.map((a) => (
                <div
                  key={a.label}
                  className="flex items-start gap-4 rounded-stitch-xl border border-stitch-border bg-stitch-canvas p-5"
                >
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-stitch-lg bg-stitch-primary-soft text-stitch-primary">
                    <span className="material-symbols-outlined">{a.icon}</span>
                  </div>
                  <div>
                    <p className="font-bold text-stitch-fg">{a.label}</p>
                    <p className="mt-0.5 text-sm text-stitch-muted">{a.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Practice-for strip ─────────────────────────────────────────── */}
        <section className="bg-stitch-canvas py-12">
          <div className="mx-auto max-w-7xl px-4 text-center">
            <p className="mb-8 text-sm font-semibold uppercase tracking-widest text-stitch-muted">
              Practice for the interviews you actually face
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {PRACTICE_FOR.map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-stitch-border bg-stitch-surface px-4 py-2 text-sm font-medium text-stitch-fg-soft transition-colors hover:border-stitch-primary/30 hover:text-stitch-primary"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── Bento features ─────────────────────────────────────────────── */}
        <section className="bg-stitch-canvas py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-2xl">
              <h2 className="hr-display mb-4 text-3xl font-bold text-stitch-fg md:text-4xl">
                Interview smarter, not harder
              </h2>
              <p className="text-lg text-stitch-muted">
                Three things that move the needle in technical and behavioral rounds — practice
                volume, honest feedback, and time with people who&apos;ve been on the other side of
                the table.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:auto-rows-fr">
              {/* Primary tile spans two columns for visual rhythm */}
              <div className="group relative overflow-hidden rounded-stitch-2xl border border-stitch-border bg-gradient-to-br from-stitch-primary to-stitch-accent p-8 text-white shadow-stitch-card transition-all duration-300 hover:shadow-stitch-pop md:col-span-2">
                <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
                <div className="relative">
                  <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-stitch-lg bg-white/15 ring-1 ring-inset ring-white/25 transition-transform group-hover:scale-110">
                    <span className="material-symbols-outlined text-3xl">psychology</span>
                  </div>
                  <h3 className="mb-3 text-2xl font-bold">AI mock interviews that adapt</h3>
                  <p className="max-w-lg leading-relaxed text-white/90">
                    The AI asks realistic, role-specific questions and follows up on your answers —
                    behavioral, coding, or system design — so every session feels like the real thing.
                  </p>
                </div>
              </div>

              <div className="group rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-8 shadow-stitch-card transition-all duration-300 hover:border-stitch-accent/20 hover:shadow-stitch-pop">
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-stitch-lg bg-stitch-accent-soft text-stitch-accent transition-transform group-hover:scale-110">
                  <span className="material-symbols-outlined text-3xl">verified_user</span>
                </div>
                <h3 className="mb-3 text-xl font-bold text-stitch-fg">Real expert coaching</h3>
                <p className="leading-relaxed text-stitch-muted">
                  Book 1:1 sessions with experienced interviewers for personalized coaching and
                  resume feedback.
                </p>
              </div>

              <div className="group rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-8 shadow-stitch-card transition-all duration-300 hover:border-stitch-primary/20 hover:shadow-stitch-pop">
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-stitch-lg bg-stitch-primary-soft text-stitch-primary transition-transform group-hover:scale-110">
                  <span className="material-symbols-outlined text-3xl">analytics</span>
                </div>
                <h3 className="mb-3 text-xl font-bold text-stitch-fg">Instant, scored feedback</h3>
                <p className="leading-relaxed text-stitch-muted">
                  Get a score and concrete, actionable tips on every answer, so you know exactly what
                  to improve before the next round.
                </p>
              </div>

              <div className="group rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-8 shadow-stitch-card transition-all duration-300 hover:border-stitch-primary/20 hover:shadow-stitch-pop md:col-span-2">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
                  <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-stitch-lg bg-stitch-success-soft text-stitch-success transition-transform group-hover:scale-110">
                    <span className="material-symbols-outlined text-3xl">trending_up</span>
                  </div>
                  <div>
                    <h3 className="mb-2 text-xl font-bold text-stitch-fg">Practice until it&apos;s muscle memory</h3>
                    <p className="leading-relaxed text-stitch-muted">
                      Repeat rounds as many times as you want. The nerves fade, your structure sharpens,
                      and the answers start coming naturally — that&apos;s the whole point.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Mid-page ad ────────────────────────────────────────────────── */}
        <section className="mx-auto max-w-7xl px-4 py-8">
          {ADS_ENABLED && !isAuthenticated ? (
            <div className="relative overflow-hidden rounded-2xl border-2 border-dashed border-stitch-border bg-stitch-surface-2 py-8">
              <AdBanner slot="home-mid" />
            </div>
          ) : (
            <div className="h-2" aria-hidden />
          )}
        </section>

        {/* ── How the free demo works — dark band ────────────────────────── */}
        <section className="bg-stitch-footer-bg py-20 text-stitch-footer-fg sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-16 max-w-xl text-left">
              <h2 className="hr-display mb-4 text-3xl font-bold md:text-4xl">How the free demo works</h2>
              <p className="text-lg text-stitch-footer-muted">
                No sign-up required. Try a real AI mock interview in under a minute.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {STEPS.map((step, i) => (
                <div
                  key={step.title}
                  className="rounded-2xl border border-stitch-border/50 bg-stitch-overlay/40 p-8 backdrop-blur-sm"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-stitch-primary/20 text-lg font-bold">
                    {i + 1}
                  </div>
                  <h3 className="mb-2 text-xl font-bold">{step.title}</h3>
                  <p className="leading-relaxed text-stitch-footer-muted">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Closing CTA ────────────────────────────────────────────────── */}
        <section className="bg-stitch-canvas px-4 py-20 sm:py-24">
          <div className="relative mx-auto max-w-5xl overflow-hidden rounded-stitch-2xl bg-gradient-to-br from-stitch-primary via-stitch-primary-hover to-stitch-accent px-6 py-14 text-center shadow-stitch-pop sm:px-12">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-stitch-accent/40 blur-3xl" />
            <div className="relative">
              <h2 className="hr-display mx-auto max-w-2xl text-3xl font-extrabold text-white sm:text-4xl">
                Your next interview is coming. Have it already behind you.
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-lg text-white/85">
                Run a free AI mock interview right now — no account, no credit card. See your first
                score in under a minute.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link
                  to="/try-interview"
                  className="inline-flex w-full items-center justify-center rounded-stitch-xl bg-white px-6 py-3.5 text-base font-bold text-stitch-primary shadow-xl transition-all hover:bg-white/90 active:scale-[0.98] sm:w-auto sm:px-8 sm:py-4 sm:text-lg"
                >
                  Start your free AI interview
                  <span className="material-symbols-outlined ml-1 text-base sm:text-lg">arrow_forward</span>
                </Link>
                <Link
                  to={isAuthenticated ? '/interviewers' : '/register'}
                  className="inline-flex items-center text-base font-semibold text-white/90 transition-colors hover:text-white"
                >
                  Browse expert coaches
                  <span className="material-symbols-outlined ml-1 text-lg">arrow_forward</span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
